var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { parseInstant, parseLocalDate } from '@rad/utility/parsers';
import { getJson, postJson, putJson, deleteJson, withDone, withOptional, withValidate, withConflict, withMap, } from '@rad/utility/http';
import { isImage, headerVariant, } from '../../types';
import { map as mapAttachments, photoOfAsset, photosOfAssets } from './attachment';
const mapResident = (x) => (Object.assign({}, x));
const onlyImages = (variants) => variants.filter((v) => v !== undefined && isImage(v));
const mapPropertyAssets = (x, parcelPhotos = []) => {
    var _a, _b, _c;
    const rawAttachments = x.map(a => ({ id: a.id, attachments: mapAttachments(a.attachments) }));
    const imageAssets = rawAttachments.filter(a => a.attachments.some(isImage));
    const photos = photosOfAssets(x).sort((a, b) => a.sortOrder - b.sortOrder);
    const headerVariants = onlyImages(imageAssets.map(headerVariant));
    const thumbnailPhoto = (_b = (_a = photos.find(p => p.isMain)) !== null && _a !== void 0 ? _a : photos[0]) !== null && _b !== void 0 ? _b : null;
    const heroPhoto = (_c = (thumbnailPhoto ? headerVariants.find(h => h.assetId === thumbnailPhoto.assetId) : null)) !== null && _c !== void 0 ? _c : thumbnailPhoto;
    return {
        photos,
        headerVariants,
        '_raw': x,
        '_raw_attachments': rawAttachments,
        'attachments': photos,
        'thumbnailPhoto': thumbnailPhoto,
        'heroPhoto': heroPhoto,
        'parcelPhotos': photosOfAssets(parcelPhotos),
    };
};
const mapParcel = (x) => (Object.assign(Object.assign({}, x), { 'created': parseInstant(x.created) }));
const mapPropertyMinimal = (meta) => (x) => (Object.assign(Object.assign({}, x), { 'point': x.point, 'kind': ((x) => x ? x : null)(x.kind && meta.kinds[x.kind]), 'communities': [
        ...meta.communities_global,
        ...((x) => x ? [x] : [])(x.community_id && meta.communities_local[x.community_id])
    ] }));
const mapPropertyMapPoint = (meta) => (x) => {
    var _a;
    return (Object.assign(Object.assign({}, x), { 'kind': x.kind ? (_a = meta.kinds[x.kind]) !== null && _a !== void 0 ? _a : null : null }));
};
const mapProperty = (x) => (Object.assign(Object.assign({}, x), { 'parcel': mapParcel(x.parcel), 'assets': mapPropertyAssets(x.assets, x.parcelPhotos), 'created': parseInstant(x.created), 'updated': parseInstant(x.updated), 'residents': x.residents.map(mapResident), 'timeline': x.timeline.map((t) => (Object.assign(Object.assign({}, t), { 'shield': false, 'start_date': parseLocalDate(t.start_date), 'end_date': parseLocalDate(t.end_date), 'assets': mapPropertyAssets(t.assets) }))) }));
export const filtersDefault = () => ({
    'page': 1,
    'pageSize': 1000,
});
const getJsonPropertiesMinimal = (path, f) => getJson(`/api/properties${path}`, Object.assign(Object.assign({}, f), { 'format': 'minimal' }));
const getJsonProperties = (path, f) => getJson(`/api/properties${path}`, f);
const mkPropertiesFetchPair = (path) => [
    (f) => withDone(withMap(getJsonProperties(path, f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))(),
    (f) => withDone(withMap(getJsonPropertiesMinimal(path, f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapPropertyMinimal(r.meta)), 'meta': undefined }))))(),
];
export const getPublishedProperties = (_a) => {
    var { status: _status } = _a, f = __rest(_a, ["status"]);
    return withDone(withMap(getJsonProperties('', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))();
};
export const getFeaturedProperties = (count) => withMap(getJson('/api/properties/featured', { count }), r => r.items.map(mapProperty));
export const getSpotlightProperty = withOptional(withMap(getJson('/api/properties/spotlight'), mapProperty));
export const getUserProperties = (f) => withDone(withMap(getJsonProperties('/user', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))();
// The properties this host's community contributed as itself.
export const getCommunityProperties = (f) => withDone(withMap(getJsonProperties('/community', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))();
export const getProperties = (f) => withDone(withMap(getJsonProperties('/admin', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))();
export const getNearbyProperties = (f) => withDone(withMap(getJsonProperties('/nearby', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapProperty) }))))();
export const [getRectangleProperties, getRectanglePropertiesMinimal] = mkPropertiesFetchPair('/rectangle');
// Explore's sidebar rows; the map's markers still come from `/rectangle`.
export const getExploreResults = (f) => withDone(withMap(getJson('/api/properties/explore', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(x => ({ 'property': mapProperty(x.property), 'view': x.view })) }))))();
const getJsonMapPoints = (path, f) => getJson(`/api/properties${path}`, Object.assign(Object.assign({}, f), { 'format': 'points' }));
export const getRectangleMapPoints = (f) => withDone(withMap(getJsonMapPoints('/rectangle', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapPropertyMapPoint(r.meta)), 'meta': undefined }))))();
const mapPropertyCard = (meta) => (x) => {
    var _a, _b;
    const kind = x.kind ? (_a = meta.kinds[x.kind]) !== null && _a !== void 0 ? _a : null : null;
    return {
        'id': x.id,
        'point': x.point,
        'count': 1,
        kind,
        'title': x.title,
        'parcel': { 'id': x.parcel_id, 'address': x.address, kind },
        'communities': [
            ...meta.communities_global,
            ...((x) => x ? [x] : [])(x.community_id && meta.communities_local[x.community_id]),
        ],
        'tags': x.tags,
        'assets': { 'thumbnailPhoto': x.photo === null ? null : (_b = photoOfAsset(x.photo)) !== null && _b !== void 0 ? _b : null, 'parcelPhotos': photosOfAssets(x.photos) },
        'claimed': x.claimed,
    };
};
const getJsonPropertyCards = (path, f) => getJson(`/api/properties${path}`, Object.assign(Object.assign({}, f), { 'format': 'card' }));
export const getRectanglePropertyCards = (f) => withDone(withMap(getJsonPropertyCards('/rectangle', f), r => (Object.assign(Object.assign({}, r), { 'items': r.items.map(mapPropertyCard(r.meta)), 'meta': undefined }))))();
// What a popup needs for one property, without the timeline, residents, quickfacts and full
// attachment set the whole record carries.
export const getPropertyCard = (id) => withDone(withOptional(withMap(getJsonPropertyCards(`/${id}/card`), r => { var _a; return (_a = r.items.map(mapPropertyCard(r.meta))[0]) !== null && _a !== void 0 ? _a : null; })))();
// The same card for a parcel nobody has contributed to yet, served off the parcel id.
export const getUnclaimedParcelCard = (id) => withDone(withOptional(withMap(getJson(`/api/parcel/${id}/card`), r => { var _a; return (_a = r.items.map(mapPropertyCard(r.meta))[0]) !== null && _a !== void 0 ? _a : null; })))();
// At or past this zoom the viewport loads full cards instead of bare points.
export const CARD_MIN_ZOOM = 18;
export const getRectangleMapItems = (f) => f.zoom >= CARD_MIN_ZOOM ? getRectanglePropertyCards(f) : getRectangleMapPoints(f);
export const getCommunityPropertyCounts = getJson('/api/properties/community-counts');
export const getQuickfacts = () => getJson('/api/properties/quickfacts');
export const getPropertyTags = () => getJson('/api/property-tags');
export const getProperty = (id) => withOptional(withMap(getJson(`/api/properties/${id}`), mapProperty));
export const getParcelView = (id) => withOptional(withMap(getJson(`/api/properties/${id}/parcel-view`), (x) => ({
    'properties': x.properties.map(mapProperty),
    'editablePropertyIds': x.editablePropertyIds,
    'ownPropertyIds': x.ownPropertyIds,
    'orgProperties': x.orgProperties,
    'orgStories': x.orgStories,
    'reviews': x.reviews,
})));
// Opens this user's own contribution on a parcel and returns its property id. Idempotent —
// contributing again to the same address returns the row they already have.
export const contribute = (parcelId) => withDone(postJson(`/api/properties/contribute/${parcelId}`, {}))();
// As `contribute`, for a geocoded address: the server registers its parcel first when nobody has.
export const contributeAtAddress = (create) => withDone(postJson('/api/properties/contribute', create))();
export const create = (f) => withDone(withValidate(withMap(postJson(`/api/properties`, f), mapProperty)))();
export const update = (f) => withDone(withValidate(withMap(putJson(`/api/properties/${f.id}`, f), mapProperty)))();
// Platform staff only, and permanent: see DELETE /api/properties/{id}.
export const deleteProperty = (id) => withDone(withConflict(deleteJson(`/api/properties/${id}`)))();
export const getStatuses = getJson(`/api/properties/statuses`);
export const setPropertyTags = (data) => withDone(putJson('/api/properties/tags', data));
export const setPropertyFeatured = (data) => withDone(putJson('/api/properties/featured', data))();
export const setTimelineFeatured = (data) => withDone(putJson('/api/properties/timelines/featured', data))();
